// SPDX-License-Identifier: AGPL-3.0-or-later

// The gateway accepts 2 voice state updates per second per session and queues the rest, but a later
// update can still bypass that queue. The client then sees its newest echo first, treats itself as
// converged, applies the stale queued echoes as server truth and writes again, which keeps the queue
// fed forever (rapid mute toggling turns into an endless mute/unmute loop). Every update carries the
// full state, so staying under the limit and coalescing per connection keeps echoes in order.
export const VOICE_STATE_SEND_LIMIT = 2;
export const VOICE_STATE_SEND_WINDOW_MS = 1100;

export interface VoiceStateSendThrottleDeps<T> {
	send: (payload: T) => boolean;
	now: () => number;
	setTimer: (callback: () => void, delayMs: number) => unknown;
	clearTimer: (handle: unknown) => void;
	onDropped?: (payload: T) => void;
}

export class VoiceStateSendThrottle<T> {
	private readonly sentAt: Array<number> = [];
	private readonly queued = new Map<string, T>();
	private timer: unknown = null;

	constructor(private readonly deps: VoiceStateSendThrottleDeps<T>) {}

	get queuedCount(): number {
		return this.queued.size;
	}

	enqueue(key: string, payload: T): boolean {
		if (this.queued.size === 0 && this.hasBudget()) {
			return this.sendNow(payload);
		}
		this.queued.delete(key);
		this.queued.set(key, payload);
		this.schedule();
		return true;
	}

	sendImmediately(key: string, payload: T): boolean {
		this.queued.delete(key);
		return this.sendNow(payload);
	}

	reset(): void {
		this.queued.clear();
		this.sentAt.length = 0;
		if (this.timer !== null) {
			this.deps.clearTimer(this.timer);
			this.timer = null;
		}
	}

	private sendNow(payload: T): boolean {
		const sent = this.deps.send(payload);
		if (sent) this.sentAt.push(this.deps.now());
		return sent;
	}

	private prune(): void {
		const cutoff = this.deps.now() - VOICE_STATE_SEND_WINDOW_MS;
		while (this.sentAt.length > 0 && this.sentAt[0] <= cutoff) this.sentAt.shift();
	}

	private hasBudget(): boolean {
		this.prune();
		return this.sentAt.length < VOICE_STATE_SEND_LIMIT;
	}

	private schedule(): void {
		if (this.timer !== null || this.queued.size === 0) return;
		this.prune();
		const delay =
			this.sentAt.length < VOICE_STATE_SEND_LIMIT
				? 0
				: Math.max(0, this.sentAt[0] + VOICE_STATE_SEND_WINDOW_MS - this.deps.now());
		this.timer = this.deps.setTimer(() => {
			this.timer = null;
			this.flush();
		}, delay);
	}

	private flush(): void {
		while (this.queued.size > 0 && this.hasBudget()) {
			const [key, payload] = this.queued.entries().next().value as [string, T];
			this.queued.delete(key);
			if (!this.sendNow(payload)) this.deps.onDropped?.(payload);
		}
		this.schedule();
	}
}
